import { UnsupportedValueError } from "@/lib/error.ts";
import { useGateValue } from "@statsig/react-bindings";
import { cn } from "@/lib/utils.ts";
import type { ThreadBrowserFrameEventData, ThreadStatusUpdateEventData } from "@/types/socketIo.ts";
import type { GetThreadResp } from "@/types/thread.ts";
import { AnimatePresence, motion } from "motion/react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { useThreadSocket } from "./hooks/useThreadSocket.ts";

/** How long a live browser preview stays up after the last frame. */
const BROWSER_FRAME_TTL_MS = 10_000;

function useElapsedTimer(isRunning: boolean) {
  const [elapsed, setElapsed] = useState(0);
  const startTimeRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isRunning) {
      setElapsed(0);
      startTimeRef.current = null;
      return;
    }

    startTimeRef.current = Date.now();
    const interval = setInterval(() => {
      if (startTimeRef.current) {
        setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isRunning]);

  return elapsed;
}

function formatElapsed(seconds: number): string {
  if (seconds < 60) return `(${seconds}s)`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `(${m}m ${s}s)`;
}

/**
 * Split a status line's trailing ellipsis off, so the elapsed timer can
 * replace the dots — "Thinking (11s)" rather than the timer dangling after
 * the ellipsis.
 */
function splitTrailingEllipsis(text: string) {
  const match = /(\.{3}|…)$/.exec(text);
  return match ? [text.slice(0, -match[0].length), match[0]] : [text, ""];
}

/**
 * Simple status text component that displays real-time status updates
 * with slide-up animation and a subtle whole-string pulse while running.
 *
 * `renderLine` receives the status line — the words and their crossfade — and
 * returns what to render in its place. The running-steps disclosure uses it to
 * make the whole line its trigger, chevron and all, so the words are as
 * clickable as the icon (BIG-8268). It wraps only the line: the browser preview
 * below is not part of the control. Wrapping from outside this component would
 * line the trigger up with the top of whatever renders first, which is the
 * preview whenever there is one.
 */
export const StatusText = ({
  thread,
  className,
  renderLine = (line) => line,
}: {
  thread: GetThreadResp;
  className?: string;
  renderLine?: (line: ReactNode) => ReactNode;
}) => {
  const isAdmin = useGateValue("internal-features");
  const isRunning = thread.activityState === "running" || thread.activityState === "stopPending";
  const elapsed = useElapsedTimer(isRunning);

  // ephemeral status updates from the socket. only relevant while running.
  const [runningStatus, setRunningStatus] = useState<ThreadStatusUpdateEventData | null>(null);
  useThreadSocket(thread.id, "thread:status_update", (data) => {
    setRunningStatus(data);
  });

  // ephemeral live browser preview frames — only while a browser-use task runs.
  // The whole event is kept, not just the image: a static page yields
  // byte-identical frames, and the lapse below has to restart on each arrival.
  const [browserFrame, setBrowserFrame] = useState<ThreadBrowserFrameEventData | null>(null);
  useThreadSocket(thread.id, "thread:browser_frame", (data) => {
    setBrowserFrame(data);
  });
  // Frames arrive every ~1.5s while the browser is working, and nothing says
  // when it stops: the turn keeps running while the agent writes up the result.
  // Let the preview lapse rather than hold the last screenshot over that.
  useEffect(() => {
    if (!browserFrame) return;
    const timer = setTimeout(() => setBrowserFrame(null), BROWSER_FRAME_TTL_MS);
    return () => clearTimeout(timer);
  }, [browserFrame]);

  // clear the status when the thread is not running. The status updates are ephemeral,
  // so they won't clear themselves automatically, but they only make sense in the context of
  // a single running state. This prevents overhang when the thread is stopped and started multiple times.
  useEffect(() => {
    if (thread.activityState !== "running") {
      setRunningStatus(null);
      setBrowserFrame(null);
    }
  }, [thread.activityState]);
  // ...and when this instance is handed another thread that is also running,
  // where the effect above would let the old thread's line and screenshot show
  // until the new one's events arrive. Reset during render so nothing paints.
  const [statusThreadId, setStatusThreadId] = useState(thread.id);
  if (thread.id !== statusThreadId) {
    setStatusThreadId(thread.id);
    setRunningStatus(null);
    setBrowserFrame(null);
  }

  // Determine what to display based on thread activity state
  let displayContent: string | null = null;
  if (["stopped", "waitingForUser", "waitingForExternalEvent"].includes(thread.activityState)) {
    // Show nothing when stopped
    displayContent = null;
  } else if (thread.activityState === "stopPending") {
    // Show "stopping..." when stop is pending
    displayContent = "Stopping...";
  } else if (thread.activityState === "running") {
    // When running, prefer socket status if available, fallback to "thinking..."
    // The status updates are ephemeral, but we should always show something for the content
    // in the running state.
    displayContent = runningStatus?.content || "Thinking...";
  } else {
    throw new UnsupportedValueError(thread.activityState as never);
  }

  const timerText = isAdmin && isRunning ? formatElapsed(elapsed) : null;
  // With a timer the line ends on it — "Thinking (11s)", no trailing dots —
  // and it inherits the status color, so it reads as part of the line rather
  // than an annotation on it. Without one the line keeps its ellipsis.
  const [statusWords, statusEllipsis] = displayContent
    ? splitTrailingEllipsis(displayContent)
    : ["", ""];

  // The muted colour sits on the container rather than the line, so a wrapper
  // can restyle the words on hover and the line inherits it.
  return (
    <div className={cn("flex flex-col items-start gap-1.5 text-muted-foreground", className)}>
      {renderLine(
        <div className="relative flex items-center">
          {/* A quick crossfade, not a slide: the outgoing line goes absolute so
              the incoming one renders in the same spot beneath it while both
              fade — text replaces text with no vertical movement. */}
          <AnimatePresence>
            <motion.div
              key={displayContent} // only animate when the actual content changes
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, position: "absolute", top: 0, left: 0 }}
              transition={{ duration: 0.15, ease: "linear" }}
              // min-h-1lh ensures we don't get a height change when displayContent becomes empty
              className="text-xs font-medium min-h-[1lh]"
            >
              {displayContent && (
                // The pulse lives on an inner span: motion owns the outer div's
                // opacity for enter/exit, and a CSS animation on the same node
                // would win that fight and cancel the fades.
                <span className="animate-pulse">
                  {statusWords}
                  {timerText ? ` ${timerText}` : statusEllipsis}
                </span>
              )}
            </motion.div>
          </AnimatePresence>
        </div>,
      )}

      {/* Live browser preview — transient, only while a browser-use task runs. */}
      <AnimatePresence>
        {isRunning && browserFrame && (
          <motion.img
            // Stable key so frame updates swap the src in place (no per-frame
            // fade); AnimatePresence only animates the preview appearing/leaving.
            key="browser-preview"
            src={browserFrame.frame}
            alt="Live browser preview"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="max-h-48 w-auto max-w-full rounded-md border object-contain"
          />
        )}
      </AnimatePresence>
    </div>
  );
};
